import theme from '^/theme';
import * as T from '^/types';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import LocationSvg from '^/assets/icons/photo-request/location.svg';
import NoImageSvg from '^/assets/icons/photo-request/no-image.svg';
import FilledChevronRightSvg from '^/assets/icons/photo-request/filled-chevron-right.svg';
import Tippy from '@tippyjs/react';
import { useL10n } from '^/hooks';
import Text from './text';
import { PhotoAlbumObject } from '^/components/three/ThreeObjects';
import { useThreeStore } from '^/components/three/ThreeStore';
import { StatusBadge } from '^/components/atoms/StatusBadge';
import NotRequestedSvg from '^/assets/icons/photo-request/not-requested.svg';
import CompletedBadgeSvg from '^/assets/icons/photo-request/request-completed.svg';
import VerticalKebabSvg from '^/assets/icons/photo-request/vertical-kebab.svg';
import DeleteSvg from '^/assets/icons/photo-request/trash.svg';
import MapCenterSvg from '^/assets/icons/map-controller/map-center.svg';
import { DropdownMenu } from '^/components/organisms/DropdownMenu';
import { usePhotoQuery } from '^/hooks/api/photos/usePhoto';

const ViewpointItem = styled.div({
  borderRadius: '8px',
  overflow: 'hidden',
  border: `1px solid ${theme.colors.coolGray[200]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px 6px 6px 6px',
  width: 'fit-content',
  position: 'relative',

  '&:hover .dropdown-menu-trigger': {
    visibility: 'visible',
  },
});

const ViewpointTitle = styled.div({
  fontSize: '12px',
  color: theme.colors.coolGray[900],
  lineHeight: '130%',
});

const ImagesContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const ImageWrapper = styled.div({
  position: 'relative',
  height: '123px',
  aspectRatio: '1/1',
  borderRadius: '4px',
  overflow: 'hidden',
});

const ViewpointImage = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const EmptyImagePlaceholder = styled.div({
  height: '123px',
  aspectRatio: '1/1',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  backgroundColor: theme.colors.coolGray[50],
});

const ImageIcon = styled.div<{ showBg?: boolean }>(({ showBg = true }) => ({
  position: 'absolute',
  top: '8px',
  left: '8px',
  width: '28px',
  height: '28px',
  backgroundColor: showBg ? theme.colors.black[50] : 'transparent',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

const ViewpointHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const MenuTriggerWrapper = styled.div({
  position: 'absolute',
  top: '4px',
  right: '4px',
  visibility: 'hidden',
  zIndex: 2,
  transition: 'visibility 0.2s',
  border: `1px solid ${theme.colorTokens.lineCool}`,
  borderRadius: '8px',
  padding: '4px',
  backgroundColor: theme.colors.white[100],
});

interface Props {
  viewpoint: T.FinalPhoto;
  index: number;
  requestStatus: T.RequestPhotoStatus;
  requestTitle: string;
  onDelete?(id: number): void;
}

export function PhotoPairItem({ viewpoint, index, requestStatus, requestTitle, onDelete }: Props) {
  const [l10n] = useL10n();
  const viewer = useThreeStore(s => s.viewer);
  const { rawPhotos } = usePhotoQuery();
  const birdEyePhoto = rawPhotos.find(p => p.viewpointPhotoId === viewpoint.id);
  const cameraControls = useThreeStore(s => s.cameraControls);

  const handleViewOnMapClick = () => {
    const photoObject = viewer?.controllers.photoAlbumController?.getObjectByName(
      viewpoint.id.toString()
    );

    if (photoObject) {
      void cameraControls?.fitToBox(photoObject, true, {
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 5,
        paddingRight: 5,
      });
    }
  };

  const handleViewpointClick = () => {
    setTimeout(() => {
      photoAlbumAnimation(viewpoint.id.toString());
    }, 500);
  };

  const handleBirdEyePhotoClick = () => {
    if (birdEyePhoto) {
      setTimeout(() => {
        photoAlbumAnimation(birdEyePhoto.id.toString());
      }, 500);
    }
  };

  const photoAlbumAnimation = (photoId: string): boolean => {
    const photoObject = viewer?.controllers.photoAlbumController?.getObjectByName(photoId);

    if (photoObject) {
      const object = photoObject as PhotoAlbumObject;
      viewer?.controllers.photoAlbumController?.setPhotoObject(object);
      return true;
    }

    return false;
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(viewpoint.id);
    }
  };

  const dropdownOptions: T.DropdownOption[] = [
    {
      label: l10n(Text.centerOnMap),
      action: handleViewOnMapClick,
      preIcon: <MapCenterSvg />,
    },
    {
      label: l10n(Text.delete),
      action: handleDelete,
      preIcon: <DeleteSvg />,
      hide: requestStatus !== T.RequestPhotoStatus.PENDING,
    },
  ];

  const droneImageStatusBadge = useMemo(() => {
    switch (requestStatus) {
      case T.RequestPhotoStatus.PENDING:
        return <NotRequestedSvg />;
      case T.RequestPhotoStatus.REQUESTED:
      case T.RequestPhotoStatus.FAILED:
        return (
          <StatusBadge type={requestStatus} testId={`drone-image-status-badge`} showText={false} />
        );
      case T.RequestPhotoStatus.COMPLETED:
        return <CompletedBadgeSvg />;
      default:
        return null;
    }
  }, [requestStatus]);

  const droneImageTippyText = useMemo(() => l10n(Text.tippyText[requestStatus]), [requestStatus]);

  const showStatusBg = useMemo(() => {
    const showBgStatuses = [T.RequestPhotoStatus.COMPLETED];
    return showBgStatuses.includes(requestStatus);
  }, [requestStatus]);

  return (
    <ViewpointItem key={viewpoint.id}>
      <ViewpointHeader>
        <ViewpointTitle>
          {requestTitle} ({index + 1})
        </ViewpointTitle>
      </ViewpointHeader>
      <MenuTriggerWrapper className="dropdown-menu-trigger">
        <DropdownMenu
          options={dropdownOptions}
          triggerStyles={{
            height: '24px',
            width: '24px',
            backgroundColor: 'transparent',
            ':hover': {
              backgroundColor: theme.colors.lightBlue[75],
            },
          }}
          optionsWrapperStyles={{
            top: '28px',
            right: '0',
          }}
        >
          <VerticalKebabSvg />
        </DropdownMenu>
      </MenuTriggerWrapper>
      <ImagesContainer>
        <ImageWrapper>
          <ViewpointImage src={viewpoint.thumbUrl} alt={index.toString()} />
          <Tippy content={l10n(Text.viewpoint)} theme="angelsw" arrow={false}>
            <ImageIcon onClick={handleViewpointClick}>
              <LocationSvg />
            </ImageIcon>
          </Tippy>
        </ImageWrapper>
        <FilledChevronRightSvg />
        <ImageWrapper>
          <Tippy content={droneImageTippyText} theme="angelsw" arrow={false}>
            <ImageIcon onClick={handleBirdEyePhotoClick} showBg={showStatusBg}>
              {droneImageStatusBadge}
            </ImageIcon>
          </Tippy>
          {birdEyePhoto ? (
            <ViewpointImage src={birdEyePhoto.thumbUrl} alt={index.toString()} />
          ) : (
            <EmptyImagePlaceholder>
              <NoImageSvg />
            </EmptyImagePlaceholder>
          )}
        </ImageWrapper>
      </ImagesContainer>
    </ViewpointItem>
  );
}
